import React, { useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Divider,
  Flex,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  SimpleGrid,
  Skeleton,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import {
  MdAccountBalance,
  MdAccountBalanceWallet,
  MdAssignment,
  MdBlock,
  MdCheckCircle,
  MdCreditCard,
  MdFileDownload,
  MdPerson,
  MdPictureAsPdf,
  MdReceiptLong,
  MdSearch,
  MdSwapHoriz,
  MdWarning,
  MdWorkOff,
} from 'react-icons/md';
import Card from 'components/card/Card';
import Button from 'components/ui/Button';
import {
  useAccountsWithDebt,
  useCloseShift,
  useDailyBox,
} from 'hooks/useBillingAccount';
import {
  useAuthorizeInvoice,
  useDownloadInvoicePdf,
  useDownloadInvoiceXml,
  useInvoices,
  useVoidInvoice,
} from 'hooks/useInvoices';
import RecordPaymentModal from 'components/layouts/patients/RecordPaymentModal';
import { IssueInvoiceModal } from './IssueInvoiceModal';
import { BillingInvoice, DailyBoxReceiptRow, DebtorAccount, InvoiceStatus } from 'types/models';
import { useNavigate } from 'react-router-dom';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtMoney(n: number | string): string {
  return `$${Number(n).toFixed(2)}`;
}

function fullName(nombres: string, apellidos: string): string {
  return [nombres, apellidos].filter(Boolean).join(' ');
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  const months = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

// ─── Shared stat card ─────────────────────────────────────────────────────────

function StatCard({ label, value, color, icon }: {
  label: string; value: string | number; color: string; icon?: React.ElementType;
}) {
  const cardBg      = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.100');
  const textColor   = useColorModeValue('gray.800', 'white');
  const mutedColor  = useColorModeValue('gray.500', 'gray.400');

  return (
    <Flex
      bg={color !== 'gray' ? `${color}.50` : cardBg}
      border="1px solid"
      borderColor={color !== 'gray' ? `${color}.100` : borderColor}
      borderRadius="14px" px="16px" py="14px"
      align="center" gap="14px"
    >
      {icon && (
        <Flex
          w="40px" h="40px" borderRadius="12px" flexShrink={0}
          bg={color !== 'gray' ? `${color}.100` : 'gray.100'}
          align="center" justify="center"
        >
          <Icon as={icon} w="20px" h="20px" color={color !== 'gray' ? `${color}.500` : mutedColor} />
        </Flex>
      )}
      <Flex direction="column" gap="2px">
        <Text fontSize="xs" fontWeight="700" textTransform="uppercase" letterSpacing="wider"
          color={color !== 'gray' ? `${color}.500` : mutedColor}>
          {label}
        </Text>
        <Text fontSize="xl" fontWeight="800" color={color !== 'gray' ? `${color}.500` : textColor}>
          {value}
        </Text>
      </Flex>
    </Flex>
  );
}

// ─── Empty state ─────────────────────────────────────────────────────────────

function EmptyState({ icon, title, subtitle }: { icon: React.ElementType; title: string; subtitle: string }) {
  const emptyBg   = useColorModeValue('gray.50', 'navy.750');
  const textColor = useColorModeValue('gray.600', 'gray.300');

  return (
    <Flex direction="column" align="center" py="56px" gap="12px">
      <Flex w="56px" h="56px" bg={emptyBg} borderRadius="16px" align="center" justify="center">
        <Icon as={icon} w="24px" h="24px" color="gray.400" />
      </Flex>
      <Text fontSize="sm" fontWeight="700" color={textColor}>{title}</Text>
      <Text fontSize="xs" color="gray.400">{subtitle}</Text>
    </Flex>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// TAB 1 — Cuentas por Cobrar (AR)
// ════════════════════════════════════════════════════════════════════════════

function DebtorRow({ account, onPay }: { account: DebtorAccount; onPay: (a: DebtorAccount) => void }) {
  const navigate    = useNavigate();
  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.100');
  const bgHover     = useColorModeValue('gray.50', 'navy.700');
  const textColor   = useColorModeValue('gray.800', 'white');
  const mutedColor  = useColorModeValue('gray.500', 'gray.400');

  const debt    = Number(account.balance.currentDebt);
  const charged = Number(account.balance.totalCharged);
  const paid    = Number(account.balance.totalPaid);
  const prof    = account.lastProfessional
    ? fullName(account.lastProfessional.nombres, account.lastProfessional.apellidos)
    : '—';

  return (
    <Flex
      align="center" gap="14px" px="20px" py="14px"
      borderBottom="1px solid" borderColor={borderColor}
      _hover={{ bg: bgHover }} transition="background 0.15s"
      flexWrap={{ base: 'wrap', md: 'nowrap' }}
    >
      <Flex w="40px" h="40px" borderRadius="12px" flexShrink={0} align="center" justify="center" bg="red.50">
        <Icon as={MdPerson} w="20px" h="20px" color="red.400" />
      </Flex>

      <Box flex="2" minW="140px">
        <Text
          fontSize="sm" fontWeight="700" color={textColor}
          cursor="pointer" _hover={{ color: 'brand.500' }} noOfLines={1}
          onClick={() => navigate(`/admin/patients/${account.patientId}`)}
        >
          {fullName(account.patient.nombres, account.patient.apellidos)}
        </Text>
        <Text fontSize="xs" color={mutedColor} mt="1px">
          {account.patient.cedula ?? 'Sin cédula'}
        </Text>
      </Box>

      <Box flex="2" minW="120px" display={{ base: 'none', lg: 'block' }}>
        <Text fontSize="xs" color={mutedColor}>Último profesional</Text>
        <Text fontSize="sm" color={textColor} fontWeight="500" noOfLines={1}>{prof}</Text>
      </Box>

      <Flex flex="3" minW="240px" gap="0" display={{ base: 'none', md: 'flex' }}>
        {[
          { label: 'Cargado', value: fmtMoney(charged), color: textColor },
          { label: 'Pagado',  value: fmtMoney(paid),    color: 'green.500' },
          { label: 'Deuda',   value: fmtMoney(debt),    color: 'red.500' },
        ].map((col, i) => (
          <Box key={col.label} flex="1" px="12px" borderLeft={i > 0 ? '1px solid' : 'none'} borderColor={borderColor}>
            <Text fontSize="10px" color={mutedColor} textTransform="uppercase" letterSpacing="wider">{col.label}</Text>
            <Text fontSize="sm" fontWeight={i === 2 ? '800' : '700'} color={col.color}>{col.value}</Text>
          </Box>
        ))}
      </Flex>

      <Badge colorScheme="red" variant="subtle" borderRadius="full" px="10px" fontSize="xs" display={{ base: 'flex', md: 'none' }}>
        {fmtMoney(debt)}
      </Badge>

      <Box flexShrink={0}>
        <Button size="sm" colorScheme="brand" borderRadius="8px" leftIcon={<Icon as={MdCreditCard} />} onClick={() => onPay(account)}>
          Registrar Pago
        </Button>
      </Box>
    </Flex>
  );
}

function CuentasPorCobrarTab() {
  const { data: accounts = [], isLoading, isError } = useAccountsWithDebt();
  const [search,   setSearch]   = useState('');
  const [selected, setSelected] = useState<DebtorAccount | null>(null);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return accounts;
    return accounts.filter(a => {
      const name = fullName(a.patient.nombres, a.patient.apellidos).toLowerCase();
      const ced  = (a.patient.cedula ?? '').toLowerCase();
      return name.includes(q) || ced.includes(q);
    });
  }, [accounts, search]);

  const totalDebt = useMemo(() => accounts.reduce((s, a) => s + Number(a.balance.currentDebt), 0), [accounts]);

  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.100');
  const textColor   = useColorModeValue('gray.800', 'white');
  const inputBg     = useColorModeValue('gray.50', 'navy.700');
  const headerBg    = useColorModeValue('gray.50', 'navy.750');

  return (
    <Box p="20px">
      {!isLoading && accounts.length > 0 && (
        <SimpleGrid columns={{ base: 1, md: 3 }} gap="12px" mb="20px">
          <StatCard label="Deudores activos" value={accounts.length}      color="orange" icon={MdWarning} />
          <StatCard label="Deuda total"       value={fmtMoney(totalDebt)} color="red"    icon={MdAccountBalance} />
          <StatCard label="Mostrando"         value={filtered.length}     color="gray"   icon={MdPerson} />
        </SimpleGrid>
      )}

      <Box bg={useColorModeValue('white', 'navy.800')} border="1px solid" borderColor={borderColor} borderRadius="14px" overflow="hidden">
        {/* Toolbar */}
        <Flex px="20px" py="14px" borderBottom="1px solid" borderColor={borderColor} align="center" justify="space-between" gap="12px" flexWrap="wrap">
          <Flex align="center" gap="10px">
            <Flex w="36px" h="36px" borderRadius="10px" bg="orange.50" align="center" justify="center" flexShrink={0}>
              <Icon as={MdWarning} w="18px" h="18px" color="orange.500" />
            </Flex>
            <Box>
              <Text fontSize="sm" fontWeight="800" color={textColor}>Deudas Pendientes</Text>
              {!isLoading && (
                <Text fontSize="xs" color="gray.400">{accounts.length} paciente{accounts.length !== 1 ? 's' : ''} con saldo pendiente</Text>
              )}
            </Box>
          </Flex>
          <InputGroup w={{ base: 'full', sm: '260px' }} size="sm">
            <InputLeftElement pointerEvents="none" h="36px">
              <Icon as={MdSearch} color="gray.400" w="16px" h="16px" />
            </InputLeftElement>
            <Input
              pl="36px" h="36px" bg={inputBg} border="1px solid" borderColor={borderColor}
              borderRadius="10px" fontSize="sm" placeholder="Buscar por nombre o cédula…"
              value={search} onChange={e => setSearch(e.target.value)}
              _focus={{ borderColor: 'brand.500', boxShadow: 'none' }}
            />
          </InputGroup>
        </Flex>

        {/* Column headers */}
        <Flex px="20px" py="8px" borderBottom="1px solid" borderColor={borderColor} bg={headerBg} display={{ base: 'none', md: 'flex' }} gap="14px" align="center">
          <Box w="40px" flexShrink={0} />
          <Text flex="2" fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider">Paciente</Text>
          <Text flex="2" fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" display={{ base: 'none', lg: 'block' }}>Último Profesional</Text>
          <Flex flex="3">
            {['Total Cargado', 'Total Pagado', 'Deuda Actual'].map((h, i) => (
              <Text key={h} flex="1" px="12px" borderLeft={i > 0 ? '1px solid' : 'none'} borderColor={borderColor}
                fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider">
                {h}
              </Text>
            ))}
          </Flex>
          <Box w="120px" flexShrink={0} />
        </Flex>

        {isLoading && (
          <Box px="20px" py="12px">
            {[1,2,3,4,5].map(i => <Skeleton key={i} h="64px" borderRadius="8px" mb="8px" opacity={0.6} />)}
          </Box>
        )}
        {!isLoading && isError && (
          <Flex direction="column" align="center" py="40px" gap="8px">
            <Text fontSize="sm" fontWeight="700" color="red.500">Error al cargar cuentas</Text>
          </Flex>
        )}
        {!isLoading && !isError && filtered.length === 0 && (
          <EmptyState
            icon={MdAccountBalance}
            title={search.trim() ? 'Sin resultados para esa búsqueda' : 'Sin deudas pendientes'}
            subtitle={search.trim() ? 'Intenta con otro nombre o cédula' : 'Todos los pacientes están al día'}
          />
        )}
        {!isLoading && !isError && filtered.map(a => (
          <DebtorRow key={a.patientId} account={a} onPay={setSelected} />
        ))}
      </Box>

      {selected && (
        <RecordPaymentModal
          isOpen
          onClose={() => setSelected(null)}
          patientId={selected.patientId}
          patientName={fullName(selected.patient.nombres, selected.patient.apellidos)}
          currentDebt={Number(selected.balance.currentDebt)}
        />
      )}
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// TAB 2 — Mi Caja Diaria
// ════════════════════════════════════════════════════════════════════════════

const METHOD_ICON: Record<string, React.ElementType> = {
  EFECTIVO:      MdAccountBalanceWallet,
  TARJETA:       MdCreditCard,
  TRANSFERENCIA: MdSwapHoriz,
};

const METHOD_COLOR: Record<string, string> = {
  EFECTIVO:      'green',
  TARJETA:       'blue',
  TRANSFERENCIA: 'purple',
};

const METHOD_LABEL: Record<string, string> = {
  EFECTIVO:      'Efectivo',
  TARJETA:       'Tarjeta',
  TRANSFERENCIA: 'Transferencia',
};

function ReceiptRow({ row }: { row: DailyBoxReceiptRow }) {
  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.100');
  const bgHover     = useColorModeValue('gray.50', 'navy.700');
  const textColor   = useColorModeValue('gray.800', 'white');
  const mutedColor  = useColorModeValue('gray.500', 'gray.400');

  const methodColor = METHOD_COLOR[row.methodType] ?? 'gray';
  const MethodIcon  = METHOD_ICON[row.methodType]  ?? MdCreditCard;

  return (
    <Flex
      align="center" gap="14px" px="20px" py="13px"
      borderBottom="1px solid" borderColor={borderColor}
      _hover={{ bg: bgHover }} transition="background 0.15s"
      flexWrap={{ base: 'wrap', md: 'nowrap' }}
    >
      {/* Method icon */}
      <Flex
        w="38px" h="38px" borderRadius="10px" flexShrink={0}
        bg={`${methodColor}.50`} align="center" justify="center"
      >
        <Icon as={MethodIcon} w="18px" h="18px" color={`${methodColor}.500`} />
      </Flex>

      {/* Hora */}
      <Box w="52px" flexShrink={0}>
        <Text fontSize="sm" fontWeight="700" color={textColor}>{fmtTime(row.paidAt)}</Text>
        <Text fontSize="10px" color={mutedColor}>{fmtDate(row.paidAt)}</Text>
      </Box>

      <Divider orientation="vertical" h="36px" />

      {/* Paciente */}
      <Box flex="2" minW="120px">
        <Text fontSize="sm" fontWeight="600" color={textColor} noOfLines={1}>
          {fullName(row.patientNombres, row.patientApellidos)}
        </Text>
        <Text fontSize="xs" color={mutedColor} noOfLines={1}>
          {row.serviceDescription ?? 'Servicio no especificado'}
        </Text>
      </Box>

      {/* Método */}
      <Badge
        colorScheme={methodColor} variant="subtle" borderRadius="full" px="10px" fontSize="10px"
        flexShrink={0} display={{ base: 'none', md: 'flex' }}
      >
        {METHOD_LABEL[row.methodType] ?? row.methodType}
      </Badge>

      {/* Referencia */}
      <Box flex="1" minW="80px" display={{ base: 'none', lg: 'block' }}>
        {row.referenceNumber ? (
          <Text fontSize="xs" color={mutedColor} fontFamily="mono" noOfLines={1}>{row.referenceNumber}</Text>
        ) : (
          <Text fontSize="xs" color="gray.300">—</Text>
        )}
      </Box>

      {/* Monto */}
      <Box textAlign="right" flexShrink={0}>
        <Text fontSize="md" fontWeight="800" color="green.500">{fmtMoney(row.amount)}</Text>
        <Text fontSize="10px" color={mutedColor}>{row.receiptNumber}</Text>
      </Box>
    </Flex>
  );
}

function MiCajaDiariaTab() {
  const { data, isLoading, isError, refetch } = useDailyBox();
  const { mutate: closeShift, isPending: closing } = useCloseShift();

  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.100');
  const textColor   = useColorModeValue('gray.800', 'white');
  const cardBg      = useColorModeValue('white', 'navy.800');
  const headerBg    = useColorModeValue('gray.50', 'navy.750');

  const totals   = data?.totals;
  const receipts = data?.receipts ?? [];

  const today = new Date().toLocaleDateString('es-EC', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  return (
    <Box p="20px">
      {/* Header + CTA */}
      <Flex align="center" justify="space-between" mb="20px" flexWrap="wrap" gap="12px">
        <Box>
          <Text fontSize="sm" fontWeight="700" color={textColor}>Resumen del día</Text>
          <Text fontSize="xs" color="gray.400" textTransform="capitalize">{today}</Text>
        </Box>
        <Button
          colorScheme="brand"
          borderRadius="10px"
          leftIcon={<Icon as={MdWorkOff} />}
          onClick={() => closeShift()}
          isLoading={closing}
          loadingText="Generando PDF…"
          size="sm"
        >
          Confirmar Cierre de Turno
        </Button>
      </Flex>

      {/* Totals by method */}
      {isLoading && (
        <SimpleGrid columns={{ base: 2, md: 4 }} gap="12px" mb="20px">
          {[1,2,3,4].map(i => <Skeleton key={i} h="80px" borderRadius="14px" />)}
        </SimpleGrid>
      )}

      {!isLoading && totals && (
        <SimpleGrid columns={{ base: 2, md: 4 }} gap="12px" mb="20px">
          <StatCard label="Efectivo"      value={fmtMoney(totals.efectivo)}      color="green"  icon={MdAccountBalanceWallet} />
          <StatCard label="Tarjeta"       value={fmtMoney(totals.tarjeta)}       color="blue"   icon={MdCreditCard} />
          <StatCard label="Transferencia" value={fmtMoney(totals.transferencia)} color="purple" icon={MdSwapHoriz} />
          <StatCard label="Total recaudado" value={fmtMoney(totals.total)}       color="brand"  icon={MdReceiptLong} />
        </SimpleGrid>
      )}

      {/* Receipts table */}
      <Box bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="14px" overflow="hidden">
        {/* Table header */}
        <Flex px="20px" py="12px" borderBottom="1px solid" borderColor={borderColor} align="center" justify="space-between">
          <Text fontSize="sm" fontWeight="800" color={textColor}>Historial de Recibos</Text>
          {!isLoading && (
            <Badge colorScheme="green" borderRadius="full" px="10px" fontSize="11px">
              {receipts.length} recibo{receipts.length !== 1 ? 's' : ''}
            </Badge>
          )}
        </Flex>

        {/* Column labels (desktop) */}
        <Flex px="20px" py="8px" borderBottom="1px solid" borderColor={borderColor} bg={headerBg}
          display={{ base: 'none', md: 'flex' }} gap="14px" align="center">
          <Box w="38px" flexShrink={0} />
          <Text w="52px" flexShrink={0} fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider">Hora</Text>
          <Divider orientation="vertical" h="16px" />
          <Text flex="2" fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider">Paciente / Servicio</Text>
          <Text w="80px" flexShrink={0} fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider"
            display={{ base: 'none', md: 'block' }}>Método</Text>
          <Text flex="1" fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider"
            display={{ base: 'none', lg: 'block' }}>Comprobante</Text>
          <Text w="80px" textAlign="right" flexShrink={0} fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider">Monto</Text>
        </Flex>

        {isLoading && (
          <Box px="20px" py="12px">
            {[1,2,3].map(i => <Skeleton key={i} h="60px" borderRadius="8px" mb="8px" opacity={0.6} />)}
          </Box>
        )}

        {!isLoading && isError && (
          <Flex direction="column" align="center" py="40px" gap="8px">
            <Text fontSize="sm" fontWeight="700" color="red.500">Error al cargar caja diaria</Text>
            <Button size="xs" variant="ghost" onClick={() => refetch()}>Reintentar</Button>
          </Flex>
        )}

        {!isLoading && !isError && receipts.length === 0 && (
          <EmptyState
            icon={MdReceiptLong}
            title="Sin recibos hoy"
            subtitle="Los cobros del día aparecerán aquí en tiempo real"
          />
        )}

        {!isLoading && !isError && receipts.map(row => (
          <ReceiptRow key={row.receiptNumber} row={row} />
        ))}
      </Box>
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// TAB 3 — Facturación Electrónica
// ════════════════════════════════════════════════════════════════════════════

const STATUS_SCHEME: Record<InvoiceStatus, string> = {
  [InvoiceStatus.EMITIDA]:    'yellow',
  [InvoiceStatus.AUTORIZADA]: 'green',
  [InvoiceStatus.ANULADA]:    'red',
};

function InvoiceRow({ invoice }: { invoice: BillingInvoice }) {
  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.100');
  const bgHover     = useColorModeValue('gray.50', 'navy.700');
  const textColor   = useColorModeValue('gray.800', 'white');
  const mutedColor  = useColorModeValue('gray.500', 'gray.400');

  const { mutate: authorize, isPending: authorizing } = useAuthorizeInvoice();
  const { mutate: voidInv,   isPending: voiding }     = useVoidInvoice();
  const { mutate: dlPdf,     isPending: pdfLoading }  = useDownloadInvoicePdf();
  const { mutate: dlXml,     isPending: xmlLoading }  = useDownloadInvoiceXml();

  const scheme = STATUS_SCHEME[invoice.invoiceStatus] ?? 'gray';

  return (
    <Flex
      align="center" gap="14px" px="20px" py="13px"
      borderBottom="1px solid" borderColor={borderColor}
      _hover={{ bg: bgHover }} transition="background 0.15s"
      flexWrap={{ base: 'wrap', md: 'nowrap' }}
    >
      <Flex w="38px" h="38px" borderRadius="10px" flexShrink={0} bg="blue.50" align="center" justify="center">
        <Icon as={MdAssignment} w="18px" h="18px" color="blue.500" />
      </Flex>

      <Box flex="1" minW="100px">
        <Text fontSize="sm" fontWeight="700" color={textColor} fontFamily="mono">
          {invoice.invoiceNumber ?? '—'}
        </Text>
        <Text fontSize="10px" color={mutedColor}>
          {invoice.emittedAt ? new Date(invoice.emittedAt).toLocaleDateString('es-EC') : '—'}
        </Text>
      </Box>

      <Box flex="2" minW="140px" display={{ base: 'none', md: 'block' }}>
        <Text fontSize="sm" color={textColor} noOfLines={1}>
          {invoice.taxInfo?.businessName ?? '—'}
        </Text>
        <Text fontSize="xs" color={mutedColor} fontFamily="mono">
          {invoice.taxInfo?.rucOrId ?? '—'}
        </Text>
      </Box>

      <Box flex="1" minW="80px" display={{ base: 'none', lg: 'block' }}>
        <Text fontSize="sm" fontWeight="700" color="green.500">
          ${Number(invoice.amount).toFixed(2)}
        </Text>
      </Box>

      <Badge colorScheme={scheme} variant="subtle" borderRadius="full" px="10px" fontSize="10px" flexShrink={0}>
        {invoice.invoiceStatus}
      </Badge>

      <Flex gap="4px" flexShrink={0}>
        <Tooltip label="Descargar PDF">
          <IconButton
            aria-label="PDF" icon={<MdPictureAsPdf />} size="sm" variant="ghost"
            colorScheme="red" isLoading={pdfLoading}
            onClick={() => dlPdf({ invoiceId: invoice.id, invoiceNumber: invoice.invoiceNumber })}
          />
        </Tooltip>
        <Tooltip label="Descargar XML">
          <IconButton
            aria-label="XML" icon={<MdFileDownload />} size="sm" variant="ghost"
            colorScheme="blue" isLoading={xmlLoading}
            onClick={() => dlXml({ invoiceId: invoice.id, invoiceNumber: invoice.invoiceNumber })}
          />
        </Tooltip>
        {invoice.invoiceStatus === InvoiceStatus.EMITIDA && (
          <Tooltip label="Autorizar con SRI">
            <IconButton
              aria-label="Autorizar" icon={<MdCheckCircle />} size="sm" variant="ghost"
              colorScheme="green" isLoading={authorizing}
              onClick={() => authorize(invoice.id)}
            />
          </Tooltip>
        )}
        {invoice.invoiceStatus !== InvoiceStatus.ANULADA && (
          <Tooltip label="Anular factura">
            <IconButton
              aria-label="Anular" icon={<MdBlock />} size="sm" variant="ghost"
              colorScheme="red" isLoading={voiding}
              onClick={() => voidInv(invoice.id)}
            />
          </Tooltip>
        )}
      </Flex>
    </Flex>
  );
}

function FacturacionTab() {
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | ''>('');
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { data: invoices = [], isLoading, isError } = useInvoices(
    statusFilter || undefined,
  );

  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.100');
  const textColor   = useColorModeValue('gray.800', 'white');
  const cardBg      = useColorModeValue('white', 'navy.800');
  const headerBg    = useColorModeValue('gray.50', 'navy.750');
  const inputBg     = useColorModeValue('gray.50', 'navy.700');

  return (
    <Box p="20px">
      {/* Summary cards */}
      {!isLoading && invoices.length > 0 && (
        <SimpleGrid columns={{ base: 2, md: 3 }} gap="12px" mb="20px">
          <StatCard label="Total facturas" value={invoices.length} color="blue" icon={MdAssignment} />
          <StatCard
            label="Autorizadas"
            value={invoices.filter(i => i.invoiceStatus === InvoiceStatus.AUTORIZADA).length}
            color="green" icon={MdCheckCircle}
          />
          <StatCard
            label="Monto total"
            value={fmtMoney(invoices.reduce((s, i) => s + Number(i.amount), 0))}
            color="brand" icon={MdReceiptLong}
          />
        </SimpleGrid>
      )}

      <Box bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="14px" overflow="hidden">
        {/* Toolbar */}
        <Flex px="20px" py="14px" borderBottom="1px solid" borderColor={borderColor}
          align="center" justify="space-between" gap="12px" flexWrap="wrap">
          <Flex align="center" gap="10px">
            <Flex w="36px" h="36px" borderRadius="10px" bg="blue.50" align="center" justify="center" flexShrink={0}>
              <Icon as={MdAssignment} w="18px" h="18px" color="blue.500" />
            </Flex>
            <Box>
              <Text fontSize="sm" fontWeight="800" color={textColor}>Facturas Electrónicas</Text>
              {!isLoading && (
                <Text fontSize="xs" color="gray.400">{invoices.length} factura{invoices.length !== 1 ? 's' : ''}</Text>
              )}
            </Box>
          </Flex>
          <Flex gap="8px" align="center" flexWrap="wrap">
            <Select
              size="sm" w="160px" h="36px" bg={inputBg} borderRadius="10px" fontSize="sm"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value as InvoiceStatus | '')}
            >
              <option value="">Todos los estados</option>
              <option value={InvoiceStatus.EMITIDA}>Emitida</option>
              <option value={InvoiceStatus.AUTORIZADA}>Autorizada</option>
              <option value={InvoiceStatus.ANULADA}>Anulada</option>
            </Select>
            <Button size="sm" colorScheme="brand" borderRadius="8px" leftIcon={<Icon as={MdAssignment} />} onClick={onOpen}>
              Emitir Factura
            </Button>
          </Flex>
        </Flex>

        {/* Column headers */}
        <Flex px="20px" py="8px" borderBottom="1px solid" borderColor={borderColor} bg={headerBg}
          display={{ base: 'none', md: 'flex' }} gap="14px" align="center">
          <Box w="38px" flexShrink={0} />
          <Text flex="1" fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider">No. Factura</Text>
          <Text flex="2" fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider"
            display={{ base: 'none', md: 'block' }}>Razón Social / Identificación</Text>
          <Text flex="1" fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider"
            display={{ base: 'none', lg: 'block' }}>Monto</Text>
          <Text w="90px" flexShrink={0} fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider">Estado</Text>
          <Text w="120px" flexShrink={0} fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider">Acciones</Text>
        </Flex>

        {isLoading && (
          <Box px="20px" py="12px">
            {[1,2,3].map(i => <Skeleton key={i} h="60px" borderRadius="8px" mb="8px" opacity={0.6} />)}
          </Box>
        )}
        {!isLoading && isError && (
          <Flex direction="column" align="center" py="40px" gap="8px">
            <Text fontSize="sm" fontWeight="700" color="red.500">Error al cargar facturas</Text>
          </Flex>
        )}
        {!isLoading && !isError && invoices.length === 0 && (
          <EmptyState
            icon={MdAssignment}
            title="Sin facturas registradas"
            subtitle="Emite una factura electrónica desde los recibos de pago"
          />
        )}
        {!isLoading && !isError && invoices.map(inv => (
          <InvoiceRow key={inv.id} invoice={inv} />
        ))}
      </Box>

      <IssueInvoiceModal isOpen={isOpen} onClose={onClose} />
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Main page — tabs
// ════════════════════════════════════════════════════════════════════════════

const TABS = [
  { label: 'Mi Caja Diaria',     icon: MdReceiptLong,    component: <MiCajaDiariaTab /> },
  { label: 'Cuentas por Cobrar', icon: MdAccountBalance, component: <CuentasPorCobrarTab /> },
  { label: 'Facturación',        icon: MdAssignment,     component: <FacturacionTab /> },
];

export default function PagosView() {
  const tabColor       = useColorModeValue('secondaryGray.600', 'secondaryGray.400');
  const activeTabColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor    = useColorModeValue('gray.100', 'whiteAlpha.100');
  const textColor      = useColorModeValue('secondaryGray.900', 'white');

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Flex direction="column" mb="24px">
        <Text color={textColor} fontSize="2xl" fontWeight="800">Pagos y Facturación</Text>
        <Text color="secondaryGray.600" fontSize="sm">
          Caja diaria, cuentas por cobrar y facturación electrónica
        </Text>
      </Flex>

      <Card p="0" overflow="hidden">
        <Tabs colorScheme="brand" isLazy>
          <Box borderBottom="1px solid" borderColor={borderColor}>
            <TabList
              px="20px" gap="0" overflowX="auto"
              sx={{ '::-webkit-scrollbar': { display: 'none' }, msOverflowStyle: 'none', scrollbarWidth: 'none' }}
            >
              {TABS.map(tab => (
                <Tab
                  key={tab.label}
                  px="16px" py="16px" fontSize="sm" fontWeight="600"
                  color={tabColor} whiteSpace="nowrap"
                  display="flex" alignItems="center" gap="6px"
                  _selected={{ color: activeTabColor, fontWeight: '800', borderBottomColor: 'brand.500', borderBottomWidth: '2px' }}
                  _focus={{ boxShadow: 'none' }}
                >
                  <Icon as={tab.icon} w="16px" h="16px" />
                  {tab.label}
                </Tab>
              ))}
            </TabList>
          </Box>
          <TabPanels>
            {TABS.map(tab => (
              <TabPanel key={tab.label} p="0">
                {tab.component}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </Card>
    </Box>
  );
}
